import React, { useEffect, useState } from "react";
import { Panel } from "../components/Panel";
import { usePlayer } from "../context/PlayerContext";
import { api, apiErr } from "../api/client";
import { CosmeticIcon } from "../components/CosmeticIcon";
import { Coins, Recycle, Sparkles, Boxes, Crown, Check, Lock, Loader2, Star, Clock } from "lucide-react";
import { Sound } from "../game/audio";
import { toast } from "sonner";

const fmt = (s) => {
  if (s <= 0) return "resetting…";
  const d = Math.floor(s / 86400), h = Math.floor((s % 86400) / 3600), m = Math.floor((s % 3600) / 60);
  return d > 0 ? `${d}d ${h}h` : h > 0 ? `${h}h ${m}m` : `${m}m`;
};

function RewardCell({ reward, cosmetics }) {
  if (!reward) return <div className="text-slate-600 text-xs">—</div>;
  if (reward.cosmetic) {
    const c = cosmetics[reward.cosmetic] || { type: "skin", color: "#888", name: reward.cosmetic };
    return (
      <div className="flex flex-col items-center gap-0.5">
        <CosmeticIcon type={c.type} color={c.color} size={34} />
        <span className="text-[9px] text-slate-300 text-center leading-tight max-w-[64px] truncate">{c.name}</span>
      </div>
    );
  }
  const items = [];
  if (reward.coins) items.push(["coins", reward.coins, Coins, "#FBBF24"]);
  if (reward.scrap) items.push(["scrap", reward.scrap, Recycle, "#34D399"]);
  if (reward.cores) items.push(["cores", reward.cores, Sparkles, "#A78BFA"]);
  if (reward.vault) items.push(["vault", reward.vault, Boxes, "#7DD3FC"]);
  return (
    <div className="flex flex-col items-center gap-0.5">
      {items.map(([k, v, Icon, col]) => (
        <div key={k} className="flex items-center gap-1 text-[11px] font-black" style={{ color: col }}>
          <Icon size={13} /> {typeof v === "number" ? `+${v}` : v}
        </div>
      ))}
    </div>
  );
}

export default function BattlePass({ onClose }) {
  const { profile, applyUser } = usePlayer();
  const [bp, setBp] = useState(null);
  const [reset, setReset] = useState(0);
  const [claiming, setClaiming] = useState(null);
  const [buying, setBuying] = useState(false);

  const load = () => api.battlepass().then(({ data }) => { setBp(data); setReset(data.seasonResetSeconds || 0); }).catch((e) => toast.error(apiErr(e)));
  useEffect(() => { load(); }, []);
  useEffect(() => { const t = setInterval(() => setReset((s) => Math.max(0, s - 1)), 1000); return () => clearInterval(t); }, []);

  const buyPremium = async () => {
    setBuying(true);
    try { const { data } = await api.checkout("battlepass_premium"); window.location.href = data.checkout_url; }
    catch (e) { toast.error(apiErr(e, "Could not start checkout.")); setBuying(false); }
  };

  const claim = async (tier, track) => {
    const key = `${tier}-${track}`;
    setClaiming(key);
    try {
      const { data } = await api.claimBattlepass(tier, track);
      applyUser(data.user);
      Sound.levelup?.();
      toast.success(`Tier ${tier} ${track} reward claimed!`);
      load();
    } catch (e) { toast.error(apiErr(e, "Could not claim.")); }
    finally { setClaiming(null); }
  };

  if (!bp) return <Panel title="Battle Pass" onClose={onClose} wide><div className="text-slate-500 text-sm py-10 text-center flex items-center justify-center gap-2"><Loader2 size={16} className="animate-spin" /> Loading Battle Pass…</div></Panel>;

  const perTier = bp.xpPerTier;
  const xpIntoTier = bp.xp % perTier;
  const tierPct = Math.min(100, (xpIntoTier / perTier) * 100);

  return (
    <Panel title="Battle Pass" onClose={onClose} wide>
      {/* header */}
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-xs uppercase tracking-widest text-amber-400 font-bold">Season Track</span>
            <span className="text-[10px] text-slate-400 flex items-center gap-1"><Clock size={11} /> resets in {fmt(reset)}</span>
          </div>
          <div className="text-slate-400 text-xs mt-0.5">Earn Pass XP every match. Tier {Math.min(bp.tierReached + 1, bp.maxTier)} in {perTier - xpIntoTier} XP.</div>
        </div>
        {bp.premium ? (
          <span className="card px-3 py-1.5 flex items-center gap-1.5 text-amber-300 font-black text-sm" data-testid="bp-premium-active"><Crown size={16} /> PREMIUM ACTIVE</span>
        ) : (
          <button onClick={buyPremium} disabled={buying} className="btn-vip py-2 px-4 text-sm flex items-center gap-1.5 disabled:opacity-60" data-testid="bp-buy-premium">{buying ? <Loader2 size={15} className="animate-spin" /> : <Crown size={15} />} Unlock Premium</button>
        )}
      </div>

      {/* overall progress */}
      <div className="card p-3 mb-4">
        <div className="flex justify-between text-[11px] text-slate-300 font-bold mb-1"><span>Tier {bp.tierReached} / {bp.maxTier}</span><span>{bp.xp.toLocaleString()} Pass XP</span></div>
        <div className="h-3 rounded-full bg-slate-800 overflow-hidden"><div className="h-full bg-gradient-to-r from-amber-500 to-orange-500 transition-all" style={{ width: `${tierPct}%` }} data-testid="bp-progress" /></div>
      </div>

      {/* track */}
      <div className="overflow-x-auto scroll-thin pb-2" data-testid="bp-track">
        <div className="flex gap-2 min-w-max">
          {bp.tiers.map((t) => {
            const unlocked = t.tier <= bp.tierReached;
            const freeClaimed = bp.claimedFree.includes(t.tier);
            const premClaimed = bp.claimedPremium.includes(t.tier);
            return (
              <div key={t.tier} className={`rounded-xl border p-1.5 w-[92px] shrink-0 ${unlocked ? "border-amber-500/40 bg-slate-900/60" : "border-slate-800 bg-slate-950/60 opacity-70"}`} data-testid={`bp-tier-${t.tier}`}>
                <div className="text-center text-[11px] font-black text-white mb-1 flex items-center justify-center gap-1">
                  {unlocked ? <Star size={11} className="text-amber-400" /> : <Lock size={10} className="text-slate-500" />} {t.tier}
                </div>
                {/* free */}
                <div className="rounded-lg bg-black/30 p-1.5 min-h-[58px] flex items-center justify-center">
                  <RewardCell reward={t.free} cosmetics={bp.cosmetics} />
                </div>
                <button disabled={!unlocked || freeClaimed || claiming} onClick={() => claim(t.tier, "free")}
                  className={`w-full mt-1 py-1 rounded-md text-[10px] font-black uppercase ${freeClaimed ? "bg-slate-800 text-slate-500" : unlocked ? "btn-primary" : "bg-slate-800 text-slate-500"}`}
                  data-testid={`bp-claim-free-${t.tier}`}>
                  {claiming === `${t.tier}-free` ? "…" : freeClaimed ? <Check size={12} className="mx-auto" /> : "Free"}
                </button>
                {/* premium */}
                <div className={`rounded-lg p-1.5 min-h-[58px] flex items-center justify-center mt-1 ${bp.premium ? "bg-fuchsia-500/10 border border-fuchsia-500/30" : "bg-black/40"}`}>
                  {bp.premium ? <RewardCell reward={t.premium} cosmetics={bp.cosmetics} /> : (
                    <div className="relative"><div className="opacity-40 blur-[1px]"><RewardCell reward={t.premium} cosmetics={bp.cosmetics} /></div><Crown size={14} className="text-fuchsia-400 absolute inset-0 m-auto" /></div>
                  )}
                </div>
                <button disabled={!unlocked || !bp.premium || premClaimed || claiming} onClick={() => claim(t.tier, "premium")}
                  className={`w-full mt-1 py-1 rounded-md text-[10px] font-black uppercase ${premClaimed ? "bg-slate-800 text-slate-500" : (unlocked && bp.premium) ? "btn-vip" : "bg-slate-800 text-slate-500"}`}
                  data-testid={`bp-claim-premium-${t.tier}`}>
                  {claiming === `${t.tier}-premium` ? "…" : premClaimed ? <Check size={12} className="mx-auto" /> : bp.premium ? "Premium" : <Lock size={11} className="mx-auto" />}
                </button>
              </div>
            );
          })}
        </div>
      </div>
      <div className="text-[10px] text-slate-500 mt-2">Premium unlocks the fuchsia track for this season. Cosmetics never affect gameplay.</div>
    </Panel>
  );
}
